<template>
  <div class="sidebar-ct" data-qa="preview-sidebar">
    <div class="header" data-qa="preview-sidebar-header">
      <div class="header-title">
        <icon glyph="navigation-content" size="18" />
        <span>
          {{ $t('CampaignForm_Preview_Sidebar_Title') }}
        </span>
      </div>
    </div>
    <div
      v-if="draftToggleVisible"
      class="campaign-preview-draft"
      data-qa="sidebar-workflow-type-toggle"
    >
      <toggle
        v-model="showDraft"
        :text="$t('CampaignForm_Preview_Workflow_ShowDraft')"
        data-qa="sidebar-workflow-type-switch"
      />
    </div>
    <div class="main-ct" data-qa="preview-sidebar-main">
      <div class="campaign-config-section">
        <sidebar-variant-selector
          v-if="model.ab"
          @variant-change="changeVariant($event)"
          :model="model"
        />
        <sidebar-workflow-push-selector
          v-if="
            extendedPreviewEnabled && model.workflow && activeChannel === 'push'
          "
          @workflow-push-change="
            $emit('workflow-action-change', {
              channel: 'push',
              payload: $event
            })
          "
          :model="model"
          :variant-key="variant"
          :workflow-type="workflowType"
        />
        <sidebar-workflow-in-app-selector
          v-if="
            extendedPreviewEnabled &&
              model.workflow &&
              activeChannel === 'inapp'
          "
          @workflow-inapp-change="
            $emit('workflow-action-change', {
              channel: 'inapp',
              payload: $event
            })
          "
          :model="model"
          :variant-key="variant"
          :workflow-type="workflowType"
        />
        <sidebar-workflow-inbox-selector
          v-if="
            extendedPreviewEnabled &&
              model.workflow &&
              activeChannel === 'inbox'
          "
          @workflow-inbox-change="
            $emit('workflow-action-change', {
              channel: 'inbox',
              payload: $event
            })
          "
          :model="model"
          :variant-key="variant"
          :workflow-type="workflowType"
        />
        <sidebar-workflow-email-selector
          v-if="model.workflow && activeChannel === 'email'"
          @workflow-email-change="
            $emit('workflow-action-change', {
              channel: 'email',
              payload: $event
            })
          "
          :model="model"
          :variant-key="variant"
          :workflow-type="workflowType"
        />
        <sidebar-language-selector
          v-if="model.languages_multi"
          @language-change="changeLanguage"
          :model="model"
        />
        <preview-content-data-tabs
          @data-change="$emit('data-change', $event)"
          @profile-change="onProfileChange"
          @profile-ready="$emit('profile-ready')"
          :model="model"
          :initial-profile="cachedProfile"
          :active-channel="activeChannel"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SidebarLanguageSelector from '@/modules/campaign-manage/components/preview/SidebarLanguageSelector.vue'
import SidebarVariantSelector from '@/modules/campaign-manage/components/preview/SidebarVariantSelector.vue'
import PreviewContentDataTabs from '@/modules/campaign-manage/components/preview/content-data-tabs/PreviewContentDataTabs.vue'
import SidebarWorkflowEmailSelector from '@/modules/campaign-manage/components/preview/SidebarWorkflowEmailSelector.vue'
import SidebarWorkflowPushSelector from '@/modules/campaign-manage/components/preview/SidebarWorkflowPushSelector.vue'
import SidebarWorkflowInAppSelector from '@/modules/campaign-manage/components/preview/SidebarWorkflowInAppSelector.vue'
import SidebarWorkflowInboxSelector from '@/modules/campaign-manage/components/preview/SidebarWorkflowInboxSelector.vue'
import Toggle from '@/common/components/form/Toggle.vue'
import { workflowType as workflowTypeEnum } from '@/modules/campaign-manage/enum/WorkflowTypeEnum'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'

export default {
  name: 'PreviewContentSidebar',

  components: {
    SidebarWorkflowEmailSelector,
    SidebarWorkflowPushSelector,
    SidebarWorkflowInAppSelector,
    SidebarWorkflowInboxSelector,
    PreviewContentDataTabs,
    SidebarVariantSelector,
    SidebarLanguageSelector,
    Toggle
  },

  props: {
    model: {
      type: Object,
      required: true
    },
    activeChannel: {
      type: String,
      required: false,
      default: 'email'
    }
  },

  data() {
    return {
      variant: 'A',
      language: 'en',
      cachedProfile: null,
      showDraft: false
    }
  },

  computed: {
    extendedPreviewEnabled() {
      return this.$hasFeature('extendedContentPreview')
    },

    draftToggleVisible() {
      return (
        this.model.type === campaignTypeEnum.TYPE_WORKFLOW &&
        this.model.new_mxa === 1 &&
        this.extendedPreviewEnabled &&
        this.$hasFeature('liveEditJourneys')
      )
    },

    workflowType() {
      return this.showDraft ? workflowTypeEnum.DRAFT : workflowTypeEnum.LIVE
    }
  },

  methods: {
    changeVariant(event) {
      this.variant = event
      this.$emit('variant-change', event)
    },

    changeLanguage(event) {
      this.language = event
      this.$emit('language-change', event)
    },

    onProfileChange(profile) {
      // Cache locally so PreviewContentDataTabs can re-seed its dropdown when the
      // sidebar is re-rendered (variant/language switch). Bubble up so the test-send
      // dialogs can show the profile label in their description.
      this.cachedProfile = profile
      this.$emit('profile-change', profile)
    }
  }
}
</script>

<style scoped>
.sidebar-ct {
  display: flex;
  flex-direction: column;
  //height: 100vh; @todo investigate and fix the height issue
  width: 350px;
  min-width: 350px;
  border-right: 1px solid #ebeef5;
}

.main-ct {
  padding: 20px 17px 0;
}

.header {
  display: flex;
  justify-content: space-between;
  height: 60px;
  border-bottom: 1px solid #ebeef5;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  line-height: 28px;
  font-weight: 600;
  color: #4b515f;
  padding: 0 21px;
}

.campaign-config-section {
  display: flex;
  flex-direction: column;
}

.campaign-preview-draft {
  padding: 20px 17px;
  border-bottom: 1px solid #ebeef5;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/PreviewContentSidebar.vue